import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card, FormInput, Button } from '../components';
import { ContractStatusStyleSetting } from '../components/ContractStatusStyleSetting';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';
import { impersonationApi } from '../services/api';
import { apiErrorText } from '../utils/apiError';
import type { AdminProfile as AdminProfileType } from '../types';

export function AdminProfile() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRequestingImpersonation, setIsRequestingImpersonation] = useState(false);
  const [showImpersonationConfirmation, setShowImpersonationConfirmation] = useState(false);
  const [impersonationEmail, setImpersonationEmail] = useState('');
  const [impersonationMessage, setImpersonationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [profile, setProfile] = useState<AdminProfileType | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    address: '',
    postalCode: '',
    city: ''
  });


  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    try {
      const data = await adminApi.getProfile();
      setProfile(data);
      setFormData({
        fullName: data.fullName || '',
        phone: data.phone || '',
        address: data.address || '',
        postalCode: data.postalCode || '',
        city: data.city || ''
      });
      setImpersonationEmail(data.lastImpersonationTargetEmail || '');
    } catch (error) {
      console.error('Failed to load profile:', error);
      setMessage({ type: 'error', text: t('adminProfile.loadError') });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    try {
      await adminApi.updateProfile({ phone: formData.phone, address: formData.address, postalCode: formData.postalCode, city: formData.city });
      setMessage({ type: 'success', text: t('adminProfile.saveSuccess') });
      await loadProfile();
    } catch (error) {
      console.error('Failed to save profile:', error);
      setMessage({ type: 'error', text: t('adminProfile.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelProfile = () => {
    if (profile) {
      setFormData({
        fullName: profile.fullName || '',
        phone: profile.phone || '',
        address: profile.address || '',
        postalCode: profile.postalCode || '',
        city: profile.city || ''
      });
    }
  };

  const openImpersonationConfirmation = (event: React.FormEvent) => {
    event.preventDefault();
    setImpersonationMessage(null);
    if (!impersonationEmail.trim() || !/^\S+@\S+\.\S+$/.test(impersonationEmail.trim())) {
      setImpersonationMessage({
        type: 'error',
        text: t('adminProfile.impersonation.invalidEmail')
      });
      return;
    }
    setShowImpersonationConfirmation(true);
  };

  const requestImpersonation = async () => {
    setIsRequestingImpersonation(true);
    setImpersonationMessage(null);
    try {
      await impersonationApi.request(impersonationEmail.trim());
      setShowImpersonationConfirmation(false);
      setImpersonationMessage({
        type: 'success',
        text: t('adminProfile.impersonation.sent', { email: profile?.email })
      });
      await loadProfile();
    } catch (error: unknown) {
      setImpersonationMessage({
        type: 'error',
        text: apiErrorText(t, error, 'adminProfile.impersonation.requestError')
      });
    } finally {
      setIsRequestingImpersonation(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminProfile.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminProfile.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminProfile.description')}</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Profile Section */}
      <Card title={t('adminProfile.profileSection')} className="mb-4">
        <form onSubmit={handleSaveProfile}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <FormInput
                name="fullName"
                disabled
                label={t('adminProfile.fields.fullName')}
                value={formData.fullName}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">{t('adminProfile.fields.email')}</label>
              <input
                type="email"
                className="form-control bg-light"
                value={profile?.email || ''}
                disabled
              />
              <small className="text-muted">{t('adminProfile.fields.emailHelp')}</small>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <FormInput
                type="tel"
                name="phone"
                label={t('adminProfile.fields.phone')}
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <AddressAutocomplete
                name="address"
                onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                label={t('adminProfile.fields.address')}
                value={formData.address}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <FormInput
                name="postalCode"
                label={t('adminProfile.fields.postalCode')}
                value={formData.postalCode}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-md-6 mb-3">
              <FormInput
                name="city"
                label={t('adminProfile.fields.city')}
                value={formData.city}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <Button type="button" variant="secondary" onClick={handleCancelProfile}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" isLoading={isSaving}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Card>

      <Card title={t('adminProfile.impersonation.title')} className="mb-4">
        <p className="text-muted">
          {t('adminProfile.impersonation.description')}
        </p>
        {impersonationMessage && (
          <div className={`alert alert-${impersonationMessage.type === 'success' ? 'success' : 'danger'}`} role="alert">
            {impersonationMessage.text}
          </div>
        )}
        <form onSubmit={openImpersonationConfirmation}>
          <div className="row align-items-end">
            <div className="col-md-8 mb-3">
              <label className="form-label" htmlFor="impersonationEmail">
                {t('adminProfile.impersonation.targetEmail')}
              </label>
              <input
                id="impersonationEmail"
                type="email"
                className="form-control"
                value={impersonationEmail}
                onChange={(event) => setImpersonationEmail(event.target.value)}
                autoComplete="off"
                required
              />
            </div>
            <div className="col-md-4 mb-3 d-grid">
              <Button type="submit">
                {t('adminProfile.impersonation.requestLink')}
              </Button>
            </div>
          </div>
        </form>

        {profile?.lastImpersonationTargetEmail && (
          <p className="small text-muted mb-3">
            {t('adminProfile.impersonation.lastRequested', {
              email: profile.lastImpersonationTargetEmail,
              date: profile.lastImpersonationRequestedAt
                ? new Date(profile.lastImpersonationRequestedAt).toLocaleString()
                : '–'
            })}
          </p>
        )}

        {showImpersonationConfirmation && (
          <div className="alert alert-warning mb-0" role="alert">
            <h6 className="fw-bold">{t('adminProfile.impersonation.confirmTitle')}</h6>
            <p>
              {t('adminProfile.impersonation.confirmText', { email: impersonationEmail.trim() })}
            </p>
            <p className="small mb-3">
              {t('adminProfile.impersonation.auditWarning')}
            </p>
            <div className="d-flex gap-2 justify-content-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowImpersonationConfirmation(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                onClick={() => void requestImpersonation()}
                isLoading={isRequestingImpersonation}
              >
                {t('adminProfile.impersonation.confirmSend')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <ContractStatusStyleSetting />

    </DashboardLayout>
  );
}
